import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ============================================================================
// ResizeObserver loop suppression — runs BEFORE React mounts so it covers every
// observer (Radix UI Select/Popover, Recharts ResponsiveContainer, Sonner
// toasts, any layout-shifting component). Two layers of defense:
//
//   1. Patch window.ResizeObserver to debounce callbacks via rAF. This breaks
//      the "callback mutates layout → triggers another callback in the same
//      tick" loop that produces the warning. This is the canonical fix used
//      across the React ecosystem (Radix, MUI, Recharts, etc).
//
//   2. Capture-phase error listeners as a safety net so CRA's
//      `react-error-overlay` never receives any leftover ResizeObserver loop
//      events — and so any future loop-prone library doesn't pop the red
//      overlay over the page (which blocks all clicks, including dropdowns).
//
// The warning is benign per the spec — it just indicates the observer
// rescheduled layout — but CRA's overlay treats every `window.onerror` as
// fatal.
// ============================================================================
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const NativeRO = window.ResizeObserver;
  window.ResizeObserver = class DebouncedResizeObserver extends NativeRO {
    constructor(callback) {
      let rafId = 0;
      const debounced = (entries, observer) => {
        if (rafId) return; // collapse multiple bursts into one frame
        rafId = window.requestAnimationFrame(() => {
          rafId = 0;
          try {
            callback(entries, observer);
          } catch (err) {
            // Re-throw on next microtask so dev tools still surface real errors,
            // but don't let it tear down the RO callback chain.
            Promise.reject(err);
          }
        });
      };
      super(debounced);
    }
  };
}

const RESIZE_OBSERVER_LOOP_ERR_RE = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

const swallowResizeObserverError = (e) => {
  const msg = (e && (e.message || (e.reason && (e.reason.message || String(e.reason))))) || "";
  if (RESIZE_OBSERVER_LOOP_ERR_RE.test(msg)) {
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
    if (typeof e.preventDefault === "function") e.preventDefault();
    return true;
  }
  return false;
};

// Capture phase so we run BEFORE react-error-overlay's bubble-phase listener.
window.addEventListener("error", swallowResizeObserverError, true);
window.addEventListener("unhandledrejection", swallowResizeObserverError, true);

// Also override console.error since react-error-overlay also hooks that for
// the "Uncaught runtime errors" overlay in some CRA versions.
const _origConsoleError = console.error;
console.error = function patchedConsoleError(...args) {
  const first = args && args[0];
  const msg = typeof first === "string" ? first : (first && first.message) || "";
  if (RESIZE_OBSERVER_LOOP_ERR_RE.test(msg)) return;
  return _origConsoleError.apply(this, args);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
